import type { PoseData } from '../../../../types';
import { isPointInPolygon } from '../../../../utils/geometry';
import type { Circle, CircleGenConfig } from './CircleTypes';
import { DEFAULT_CIRCLE_CONFIG } from './CircleTypes';

export interface Point {
  x: number;
  y: number;
}

/**
 * Compute the torso polygon vertices in normalized coordinates.
 *
 * The polygon is a pentagon: a triangle point above the shoulders, the two
 * shoulders, and two hips. Hip positions are derived from visible hips, mirrored
 * from a single visible hip, or assumed from shoulder width when no hips are visible.
 *
 * Returns null if both shoulders are not visible (torso cannot be drawn).
 *
 * Validates Requirements 3.3.4.1, 3.3.4.6
 */
export function computeTorsoPolygon(landmarks: PoseData['landmarks']): Point[] | null {
  const leftShoulder = landmarks.leftShoulder;
  const rightShoulder = landmarks.rightShoulder;

  if (leftShoulder.visibility < 0.5 || rightShoulder.visibility < 0.5) {
    return null;
  }

  const shoulderMidX = (leftShoulder.x + rightShoulder.x) / 2;
  const shoulderMidY = (leftShoulder.y + rightShoulder.y) / 2;
  const shoulderDx = rightShoulder.x - leftShoulder.x;
  const shoulderDy = rightShoulder.y - leftShoulder.y;
  const shoulderWidth = Math.sqrt(shoulderDx * shoulderDx + shoulderDy * shoulderDy);

  const trianglePointX = shoulderMidX;
  const trianglePointY = shoulderMidY - shoulderWidth * 0.15;

  const leftHipVisible = landmarks.leftHip.visibility >= 0.5;
  const rightHipVisible = landmarks.rightHip.visibility >= 0.5;
  const bothHipsVisible = leftHipVisible && rightHipVisible;
  const oneHipVisible = leftHipVisible !== rightHipVisible;

  let leftHipX: number, leftHipY: number, rightHipX: number, rightHipY: number;

  if (bothHipsVisible) {
    leftHipX = landmarks.leftHip.x;
    leftHipY = landmarks.leftHip.y;
    rightHipX = landmarks.rightHip.x;
    rightHipY = landmarks.rightHip.y;
  } else if (oneHipVisible) {
    if (leftHipVisible) {
      leftHipX = landmarks.leftHip.x;
      leftHipY = landmarks.leftHip.y;
      const hipOffset = leftHipX - shoulderMidX;
      rightHipX = shoulderMidX - hipOffset;
      rightHipY = leftHipY;
    } else {
      rightHipX = landmarks.rightHip.x;
      rightHipY = landmarks.rightHip.y;
      const hipOffset = rightHipX - shoulderMidX;
      leftHipX = shoulderMidX - hipOffset;
      leftHipY = rightHipY;
    }
  } else {
    // No hips visible - assume hip width ~0.7x and torso length ~1.5x shoulder width
    const assumedHipWidth = shoulderWidth * 0.7;
    const assumedTorsoLength = shoulderWidth * 1.5;
    const assumedHipY = shoulderMidY + assumedTorsoLength;
    leftHipX = shoulderMidX - assumedHipWidth / 2;
    leftHipY = assumedHipY;
    rightHipX = shoulderMidX + assumedHipWidth / 2;
    rightHipY = assumedHipY;
  }

  return [
    { x: trianglePointX, y: trianglePointY },
    { x: leftShoulder.x, y: leftShoulder.y },
    { x: leftHipX, y: leftHipY },
    { x: rightHipX, y: rightHipY },
    { x: rightShoulder.x, y: rightShoulder.y },
  ];
}

/** Axis-aligned bounding box of a polygon. */
export function boundingBox(vertices: Point[]): { minX: number; maxX: number; minY: number; maxY: number } {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const v of vertices) {
    minX = Math.min(minX, v.x);
    maxX = Math.max(maxX, v.x);
    minY = Math.min(minY, v.y);
    maxY = Math.max(maxY, v.y);
  }
  return { minX, maxX, minY, maxY };
}

/**
 * Generate filled torso circles within the torso polygon.
 *
 * Every circle position is verified to lie inside the polygon via ray casting,
 * so all returned circles are guaranteed contained within the torso bounds.
 *
 * @param landmarks Pose landmarks
 * @param config Circle generation parameters
 * @param random RNG in [0, 1); injectable for deterministic tests
 * @returns Array of torso fill circles (empty if shoulders not visible)
 *
 * Validates Requirements 3.3.4.1, 3.3.4.6
 */
export function generateTorsoCircles(
  landmarks: PoseData['landmarks'],
  config: CircleGenConfig = DEFAULT_CIRCLE_CONFIG,
  random: () => number = Math.random
): Circle[] {
  const vertices = computeTorsoPolygon(landmarks);
  if (!vertices) {
    return [];
  }

  const { minX, maxX, minY, maxY } = boundingBox(vertices);
  const polygonWidth = maxX - minX;
  const polygonHeight = maxY - minY;

  if (polygonWidth <= 0 || polygonHeight <= 0) {
    return [];
  }

  // Estimate circle count from polygon area (pixels at reference width)
  const referenceWidth = 1920;
  const polygonAreaPixels = polygonWidth * polygonHeight * referenceWidth * referenceWidth;
  const circleAreaPixels = Math.PI * (config.baseCircleSize / 2) * (config.baseCircleSize / 2);
  const targetCircles = Math.floor(polygonAreaPixels / (circleAreaPixels * 2));
  const numCircles = Math.max(30, Math.min(50, targetCircles));

  const circles: Circle[] = [];

  const gridCols = Math.ceil(Math.sqrt(numCircles * (polygonWidth / polygonHeight)));
  const gridRows = Math.ceil(numCircles / gridCols);

  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      const baseX = minX + (col + 0.5) * (polygonWidth / gridCols);
      const baseY = minY + (row + 0.5) * (polygonHeight / gridRows);

      const randomOffsetX = (random() - 0.5) * 2 * config.torsoGridRandomness * (polygonWidth / gridCols);
      const randomOffsetY = (random() - 0.5) * 2 * config.torsoGridRandomness * (polygonHeight / gridRows);

      const x = baseX + randomOffsetX;
      const y = baseY + randomOffsetY;

      // Only keep positions inside the polygon - guarantees containment
      if (!isPointInPolygon({ x, y }, vertices)) {
        continue;
      }

      const growthDuration = config.minGrowthDuration +
        random() * (config.maxGrowthDuration - config.minGrowthDuration);
      const baseGrowthRate = config.baseCircleSize / growthDuration;
      const growthRateVariation = (random() - 0.5) * 2 * config.growthRateVariation;
      const growthRate = baseGrowthRate * (1 + growthRateVariation);
      const staggeredAge = -(circles.length / numCircles) * growthDuration;

      circles.push({
        position: { x, y },
        currentSize: config.baseCircleSize * config.startSizeFactor,
        maxSize: config.baseCircleSize,
        growthRate,
        age: staggeredAge,
        growthDuration,
        relativePosition: {
          x: (x - minX) / polygonWidth,
          y: (y - minY) / polygonHeight,
        },
      });

      if (circles.length >= numCircles) {
        return circles;
      }
    }
  }

  return circles;
}
